import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  Briefcase, Loader2, CheckCircle2, XCircle, ArrowLeft, FileSignature, Copy, TrendingUp, Search
} from "lucide-react";

const brl = (v) => (Number(v) || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const isoToBR = (s) => {
  if (!s) return "";
  const m = String(s).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : String(s);
};
const sexoGen = (s) => { const u = (s || "").toUpperCase(); if (u.startsWith("M")) return "M"; if (u.startsWith("F")) return "F"; return ""; };
const ecCode = (s) => { const u = (s || "").toUpperCase(); if (u.includes("SOLT")) return "1"; if (u.includes("CAS")) return "2"; if (u.includes("DIVOR")) return "3"; if (u.includes("VI")) return "4"; return ""; };
const normCel = (v) => { let d = String(v || "").replace(/\D/g, ""); if (d.startsWith("55") && d.length > 11) d = d.slice(2); if (d.length > 11) d = d.slice(-11); return d; };

export default function ProspectaSimulacaoModal({ open, onClose, cpf, telefone, nome, cliente }) {
  const [step, setStep] = useState("dados");
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ cpf: "", nome: "", data_nascimento: "", nome_mae: "", celular: "", email: "" });
  const [resultado, setResultado] = useState(null);     // resposta da simulação
  const [oferta, setOferta] = useState(null);           // oferta escolhida {simulationId, offer}
  const [digit, setDigit] = useState({
    banco: "", agencia: "", conta: "", contaDigito: "", tipoConta: "CC", operationType: "Pix",
    rg: "", orgao_emissor: "", uf_emissor: "", data_expedicao: "", genero: "", estado_civil: "",
    endereco_cep: "", endereco_rua: "", endereco_numero: "", endereco_bairro: "", endereco_cidade: "", endereco_uf: "",
  });
  const [proposta, setProposta] = useState(null);

  useEffect(() => {
    if (!open) return;
    const c = cliente || {};
    const end = c.endereco || {};
    const conta = (c.contas_bancarias || []).find((x) => x.principal) || (c.contas_bancarias || [])[0] || {};
    const email = ((c.emails || []).find((x) => x.principal) || (c.emails || [])[0] || {}).email || "";
    const orgaoRaw = c.orgao_expedidor || "";
    const orgaoP = orgaoRaw.includes("/") ? orgaoRaw.split("/")[0] : orgaoRaw;
    const ufP = orgaoRaw.includes("/") ? orgaoRaw.split("/")[1] : (end.estado || "");
    setStep("dados"); setResultado(null); setOferta(null); setProposta(null);
    setForm({
      cpf: (c.cpf || cpf || "").replace(/\D/g, ""),
      nome: c.nome || nome || "",
      data_nascimento: isoToBR(c.data_nascimento),
      nome_mae: c.nome_mae || "",
      celular: normCel(telefone || (c.telefones || [])[0]?.numero || ""),
      email,
    });
    setDigit({
      banco: conta.banco || "", agencia: conta.agencia || "", conta: conta.conta || "",
      contaDigito: "", tipoConta: (conta.tipo_conta || "").toUpperCase().startsWith("POUP") ? "CP" : "CC",
      operationType: "Pix",
      rg: c.rg || "", orgao_emissor: (orgaoP || "").trim(), uf_emissor: (ufP || "").trim(),
      data_expedicao: isoToBR(c.data_emissao_rg), genero: sexoGen(c.sexo), estado_civil: ecCode(c.estado_civil),
      endereco_cep: end.cep || "", endereco_rua: end.logradouro || "", endereco_numero: end.numero || "",
      endereco_bairro: end.bairro || "", endereco_cidade: end.cidade || "", endereco_uf: end.estado || "",
    });
  }, [open, cpf, telefone, nome, cliente]);

  const simular = async () => {
    if (!form.cpf || form.cpf.replace(/\D/g, "").length !== 11) { toast.error("CPF inválido"); return; }
    if (!form.nome || !form.data_nascimento || !form.nome_mae) { toast.error("Preencha nome, data de nascimento e nome da mãe"); return; }
    setLoading(true);
    try {
      const r = await axios.post(`${API}/prospecta/simular`, { ...form, celular: normCel(form.celular) });
      setResultado(r.data);
      setStep("ofertas");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha na simulação");
    } finally { setLoading(false); }
  };

  const aceitarOferta = async (simulationId, offer) => {
    setLoading(true);
    try {
      await axios.post(`${API}/prospecta/selecionar-oferta`, { simulationId, offerId: offer.offerId });
      setOferta({ simulationId, offer });
      setStep("digitar");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao selecionar oferta");
    } finally { setLoading(false); }
  };

  const digitar = async () => {
    if (!digit.banco || !digit.agencia || !digit.conta) { toast.error("Informe banco, agência e conta"); return; }
    setLoading(true);
    try {
      const cliente_extra = {
        rg: digit.rg, orgao_emissor: digit.orgao_emissor, uf_emissor: digit.uf_emissor,
        data_expedicao: digit.data_expedicao, genero: digit.genero, estado_civil: digit.estado_civil,
        endereco_cep: digit.endereco_cep, endereco_rua: digit.endereco_rua, endereco_numero: digit.endereco_numero,
        endereco_bairro: digit.endereco_bairro, endereco_cidade: digit.endereco_cidade, endereco_uf: digit.endereco_uf,
      };
      const banco_dados = {
        banco: digit.banco, agencia: digit.agencia, conta: digit.conta,
        contaDigito: digit.contaDigito, tipoConta: digit.tipoConta,
      };
      const r = await axios.post(`${API}/prospecta/digitar`, { cpf: form.cpf, simulationId: oferta.simulationId, cliente_extra, banco_dados });
      setProposta(r.data);
      setStep("resultado");
      toast.success("Proposta digitada!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao digitar proposta");
    } finally { setLoading(false); }
  };

  const setF = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const setD = (k, v) => setDigit((p) => ({ ...p, [k]: v }));

  const [buscandoNV, setBuscandoNV] = useState(false);
  const buscarNovaVida = async () => {
    const cid = cliente?.id;
    if (!cid) { toast.error("Cliente não vinculado para consulta Nova Vida"); return; }
    setBuscandoNV(true);
    try {
      const r = await axios.post(`${API}/clientes/${cid}/nova-vida`);
      if (r.data?.bloqueado) toast.info("Já consultado na Nova Vida nos últimos 12 meses.");
      else if (r.data?.ok) toast.success(`Dados Nova Vida atualizados (${(r.data.campos_atualizados || []).length} campo(s)).`);
      const cpfDigits = (form.cpf || cpf || "").replace(/\D/g, "");
      const rc = await axios.get(`${API}/wpp/buscar-cliente-por-telefone`, { params: { telefone: telefone || form.celular || "", cpf: cpfDigits } });
      if (rc.data?.found) {
        const c = rc.data.cliente || {};
        const end = c.endereco || {};
        const email = ((c.emails || []).find((x) => x.principal) || (c.emails || [])[0] || {}).email || "";
        const orgaoRaw = c.orgao_expedidor || "";
        const orgaoP = orgaoRaw.includes("/") ? orgaoRaw.split("/")[0] : orgaoRaw;
        const ufP = orgaoRaw.includes("/") ? orgaoRaw.split("/")[1] : (end.estado || "");
        setForm((pp) => ({ ...pp,
          cpf: (c.cpf || pp.cpf || "").replace(/\D/g, ""),
          nome: c.nome || pp.nome || "",
          data_nascimento: isoToBR(c.data_nascimento) || pp.data_nascimento,
          nome_mae: c.nome_mae || pp.nome_mae || "",
          email: email || pp.email,
        }));
        setDigit((pp) => ({ ...pp,
          rg: c.rg || pp.rg, orgao_emissor: (orgaoP || pp.orgao_emissor || "").trim(), uf_emissor: (ufP || pp.uf_emissor || "").trim(),
          data_expedicao: isoToBR(c.data_emissao_rg) || pp.data_expedicao, estado_civil: ecCode(c.estado_civil) || pp.estado_civil,
          endereco_cep: end.cep || pp.endereco_cep, endereco_rua: end.logradouro || pp.endereco_rua, endereco_numero: end.numero || pp.endereco_numero,
          endereco_bairro: end.bairro || pp.endereco_bairro, endereco_cidade: end.cidade || pp.endereco_cidade, endereco_uf: end.estado || pp.endereco_uf,
        }));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao consultar Nova Vida");
    } finally { setBuscandoNV(false); }
  };

  const bancoUy3 = (resultado?.banks || []).find((b) => b.bankId === "uy3") || (resultado?.banks || [])[0];

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="prospecta-sim-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Briefcase className="h-5 w-5 text-violet-600" /> Simulação Prospecta CLT (UY3 / Paypro)
          </DialogTitle>
        </DialogHeader>

        {/* Stepper */}
        <div className="flex items-center gap-2 text-[11px] font-medium">
          {["dados", "ofertas", "digitar", "resultado"].map((s, i) => (
            <div key={s} className={`flex items-center gap-1 ${step === s ? "text-violet-700" : "text-gray-400"}`}>
              <span className={`h-5 w-5 rounded-full flex items-center justify-center ${step === s ? "bg-violet-600 text-white" : "bg-gray-100"}`}>{i + 1}</span>
              <span className="capitalize">{s === "dados" ? "Dados" : s === "ofertas" ? "Ofertas" : s === "digitar" ? "Digitação" : "Resultado"}</span>
              {i < 3 && <span className="mx-1">→</span>}
            </div>
          ))}
        </div>

        {/* STEP DADOS */}
        {step === "dados" && (
          <div className="space-y-3" data-testid="step-dados">
            <p className="text-xs text-gray-500">Confirme/colete os dados do cliente para simular no UY3.</p>
            {cliente?.id && (
              <Button type="button" variant="outline" size="sm" onClick={buscarNovaVida} disabled={buscandoNV}
                className="gap-2 border-blue-500 text-blue-600 hover:bg-blue-50" data-testid="btn-prospecta-nova-vida">
                {buscandoNV ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                {buscandoNV ? "Consultando Nova Vida..." : "Buscar dados (Nova Vida)"}
              </Button>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">CPF *</Label><Input value={form.cpf} onChange={(e) => setF("cpf", e.target.value)} data-testid="sim-cpf" /></div>
              <div><Label className="text-xs">Celular</Label><Input value={form.celular} onChange={(e) => setF("celular", e.target.value)} data-testid="sim-celular" /></div>
              <div className="col-span-2"><Label className="text-xs">Nome completo *</Label><Input value={form.nome} onChange={(e) => setF("nome", e.target.value)} data-testid="sim-nome" /></div>
              <div><Label className="text-xs">Data nascimento * (DD/MM/AAAA)</Label><Input placeholder="19/10/1978" value={form.data_nascimento} onChange={(e) => setF("data_nascimento", e.target.value)} data-testid="sim-nasc" /></div>
              <div><Label className="text-xs">Nome da mãe *</Label><Input value={form.nome_mae} onChange={(e) => setF("nome_mae", e.target.value)} data-testid="sim-mae" /></div>
              <div className="col-span-2"><Label className="text-xs">E-mail</Label><Input value={form.email} onChange={(e) => setF("email", e.target.value)} data-testid="sim-email" /></div>
            </div>
            <Button onClick={simular} disabled={loading} className="w-full gap-2 bg-violet-600 hover:bg-violet-700" data-testid="btn-simular">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <TrendingUp className="h-4 w-4" />} Simular UY3
            </Button>
          </div>
        )}

        {/* STEP OFERTAS - linha do tempo por banco */}
        {step === "ofertas" && (
          <div className="space-y-3" data-testid="step-ofertas">
            {(resultado?.banks || []).length > 0 ? (
              <>
                <p className="text-xs text-gray-500">Linha do tempo da consulta — {(resultado.banks).length} banco(s):</p>
                {resultado.banks.map((bk, idx) => {
                  const aprovado = (bk.offers || []).length > 0;
                  const naoConsultado = ["TIMEOUT", "ERROR", "SKIPPED", "NO_DATA"].includes((bk.status || "").toUpperCase());
                  const badgeTxt = aprovado ? `${bk.offers.length} oferta(s)` : (naoConsultado ? "Não consultado" : "Reprovado");
                  const badgeCls = aprovado ? "bg-green-100 text-green-700" : (naoConsultado ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-600");
                  return (
                    <div key={bk.bankId || idx} className="border rounded-lg p-3" data-testid={`banco-${bk.bankId || idx}`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-semibold text-sm flex items-center gap-1">
                          {aprovado ? <CheckCircle2 className="h-4 w-4 text-green-600" /> : <XCircle className={`h-4 w-4 ${naoConsultado ? "text-amber-400" : "text-red-400"}`} />}
                          {bk.bankName || bk.bankId}
                        </span>
                        <Badge className={badgeCls}>
                          {badgeTxt}
                        </Badge>
                      </div>
                      {aprovado ? (
                        <div className="space-y-2">
                          {bk.offers.map((of) => (
                            <div key={of.offerId} className="border rounded p-2 hover:border-violet-400 transition-colors" data-testid={`oferta-${of.offerId}`}>
                              <div className="grid grid-cols-4 gap-2 text-center mb-2">
                                <div><p className="text-[10px] text-gray-400">Parcelas</p><p className="font-bold">{of.installments}x</p></div>
                                <div><p className="text-[10px] text-gray-400">Parcela</p><p className="font-bold">{brl(of.installmentAmount)}</p></div>
                                <div><p className="text-[10px] text-gray-400">Líquido</p><p className="font-bold text-green-700">{brl(of.liquidAmount)}</p></div>
                                <div><p className="text-[10px] text-gray-400">Taxa a.m.</p><p className="font-bold">{of.interestRate}%</p></div>
                              </div>
                              <Button size="sm" disabled={loading} onClick={() => aceitarOferta(bk.simulationId, of)} className="w-full bg-green-600 hover:bg-green-700 gap-1" data-testid={`btn-aceitar-${of.offerId}`}>
                                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Cliente aceitou esta oferta
                              </Button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-xs text-gray-500">{bk.reason || "Banco não retornou oferta para este CPF."}</p>
                      )}
                    </div>
                  );
                })}
              </>
            ) : (
              <div className="text-center py-6" data-testid="oferta-recusada">
                <XCircle className="h-10 w-10 mx-auto text-red-400 mb-2" />
                <p className="font-medium text-red-600">Sem oferta em nenhum banco</p>
                <p className="text-sm text-gray-500">
                  {(Array.isArray(resultado?.barriers) && resultado.barriers.length > 0)
                    ? resultado.barriers.map((b) => (typeof b === "string" ? b : (b?.message || b?.description || JSON.stringify(b)))).join(" • ")
                    : (resultado?.blocked
                        ? "Cliente bloqueado para simulação no momento."
                        : "Nenhum banco retornou oferta para este CPF no momento (sem margem/elegibilidade).")}
                </p>
                <p className="text-[11px] text-gray-400 mt-2">Consulta registrada e lead marcado como REJEITADO na Prospecta.</p>
              </div>
            )}
            <Button variant="ghost" size="sm" onClick={() => setStep("dados")} className="gap-1"><ArrowLeft className="h-4 w-4" /> Voltar para os dados</Button>
          </div>
        )}

        {/* STEP DIGITAR */}
        {step === "digitar" && (
          <div className="space-y-3" data-testid="step-digitar">
            <div className="bg-violet-50 rounded-lg p-2 text-xs text-violet-700">
              Oferta: {oferta?.offer?.installments}x de {brl(oferta?.offer?.installmentAmount)} · Líquido {brl(oferta?.offer?.liquidAmount)}
            </div>
            <p className="text-xs font-semibold text-gray-600">Conta bancária (crédito)</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label className="text-xs">Banco *</Label><Input placeholder="Itaú" value={digit.banco} onChange={(e) => setD("banco", e.target.value)} data-testid="dig-banco" /></div>
              <div><Label className="text-xs">Agência *</Label><Input value={digit.agencia} onChange={(e) => setD("agencia", e.target.value)} data-testid="dig-agencia" /></div>
              <div className="grid grid-cols-3 gap-1">
                <div className="col-span-2"><Label className="text-xs">Conta *</Label><Input value={digit.conta} onChange={(e) => setD("conta", e.target.value)} data-testid="dig-conta" /></div>
                <div><Label className="text-xs">Díg.</Label><Input value={digit.contaDigito} onChange={(e) => setD("contaDigito", e.target.value)} data-testid="dig-digito" /></div>
              </div>
              <div><Label className="text-xs">Tipo</Label>
                <Select value={digit.tipoConta} onValueChange={(v) => setD("tipoConta", v)}>
                  <SelectTrigger data-testid="dig-tipo"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="CC">Corrente</SelectItem><SelectItem value="CP">Poupança</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Recebimento</Label>
                <Select value={digit.operationType} onValueChange={(v) => setD("operationType", v)}>
                  <SelectTrigger data-testid="dig-oper"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="Pix">Pix</SelectItem><SelectItem value="Ted">Ted</SelectItem><SelectItem value="TitularidadeEmpregador">Titular. Empregador</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <p className="text-xs font-semibold text-gray-600 pt-1">Documentos / endereço (exigidos na digitação)</p>
            <div className="grid grid-cols-3 gap-2">
              <div><Label className="text-xs">RG</Label><Input value={digit.rg} onChange={(e) => setD("rg", e.target.value)} data-testid="dig-rg" /></div>
              <div><Label className="text-xs">Órgão</Label><Input placeholder="SSP" value={digit.orgao_emissor} onChange={(e) => setD("orgao_emissor", e.target.value)} /></div>
              <div><Label className="text-xs">UF emissor</Label><Input placeholder="SP" value={digit.uf_emissor} onChange={(e) => setD("uf_emissor", e.target.value)} /></div>
              <div><Label className="text-xs">Expedição</Label><Input placeholder="15/03/2005" value={digit.data_expedicao} onChange={(e) => setD("data_expedicao", e.target.value)} /></div>
              <div><Label className="text-xs">Gênero</Label>
                <Select value={digit.genero} onValueChange={(v) => setD("genero", v)}><SelectTrigger><SelectValue placeholder="-" /></SelectTrigger>
                  <SelectContent><SelectItem value="M">M</SelectItem><SelectItem value="F">F</SelectItem></SelectContent></Select>
              </div>
              <div><Label className="text-xs">Estado civil</Label>
                <Select value={digit.estado_civil} onValueChange={(v) => setD("estado_civil", v)}><SelectTrigger><SelectValue placeholder="-" /></SelectTrigger>
                  <SelectContent><SelectItem value="1">Solteiro</SelectItem><SelectItem value="2">Casado</SelectItem><SelectItem value="3">Divorciado</SelectItem><SelectItem value="4">Viúvo</SelectItem></SelectContent></Select>
              </div>
              <div><Label className="text-xs">CEP</Label><Input value={digit.endereco_cep} onChange={(e) => setD("endereco_cep", e.target.value)} /></div>
              <div className="col-span-2"><Label className="text-xs">Rua</Label><Input value={digit.endereco_rua} onChange={(e) => setD("endereco_rua", e.target.value)} /></div>
              <div><Label className="text-xs">Número</Label><Input value={digit.endereco_numero} onChange={(e) => setD("endereco_numero", e.target.value)} /></div>
              <div><Label className="text-xs">Bairro</Label><Input value={digit.endereco_bairro} onChange={(e) => setD("endereco_bairro", e.target.value)} /></div>
              <div><Label className="text-xs">Cidade</Label><Input value={digit.endereco_cidade} onChange={(e) => setD("endereco_cidade", e.target.value)} /></div>
              <div><Label className="text-xs">UF</Label><Input value={digit.endereco_uf} onChange={(e) => setD("endereco_uf", e.target.value)} /></div>
            </div>
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={() => setStep("ofertas")} className="gap-1"><ArrowLeft className="h-4 w-4" /> Voltar</Button>
              <Button onClick={digitar} disabled={loading} className="flex-1 gap-2 bg-violet-600 hover:bg-violet-700" data-testid="btn-digitar">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSignature className="h-4 w-4" />} Digitar proposta
              </Button>
            </div>
          </div>
        )}

        {/* STEP RESULTADO */}
        {step === "resultado" && proposta && (
          <div className="text-center py-4 space-y-3" data-testid="step-resultado">
            <CheckCircle2 className="h-12 w-12 mx-auto text-green-500" />
            <p className="text-lg font-bold">Proposta digitada!</p>
            {proposta.proposalNumber && <p className="text-sm">Nº contrato: <span className="font-mono font-bold">{proposta.proposalNumber}</span></p>}
            <Badge className="bg-blue-100 text-blue-700">{proposta.statusNormalizado || proposta.status}</Badge>
            {proposta.signUrl && (
              <div className="flex items-center gap-2 justify-center">
                <a href={proposta.signUrl} target="_blank" rel="noopener noreferrer" className="text-violet-600 underline text-sm flex items-center gap-1">
                  <FileSignature className="h-4 w-4" /> Link de assinatura
                </a>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { navigator.clipboard.writeText(proposta.signUrl); toast.success("Link copiado!"); }}><Copy className="h-3.5 w-3.5" /></Button>
              </div>
            )}
            <p className="text-[11px] text-gray-400">Lead marcado como CONVERTIDO na Prospecta automaticamente.</p>
            <Button onClick={onClose} className="w-full" data-testid="btn-fechar-resultado">Concluir</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
